import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import CheckPicker from "rsuite/CheckPicker";
// import "rsuite/dist/rsuite.css";
import "../../../assets/css/rsuite.css";

// (Optional) Import component styles. If you are using Less, import the `index.less` file.
import "rsuite/CheckPicker/styles/index.css";
import { BsFilter } from "react-icons/bs";

import Input from "rsuite/Input";
import InputGroup from "rsuite/InputGroup";
import SearchIcon from "@rsuite/icons/Search";
import DateRangePicker from "rsuite/DateRangePicker";
import { addDays } from "date-fns";
import moment from "moment";

const predefinedRanges = [
	{
		label: "Today",
		value: [new Date(), new Date()],
		placement: "left",
	},
	{
		label: "Yesterday",
		value: [addDays(new Date(), -1), addDays(new Date(), -1)],
		placement: "left",
	},
	{
		label: "Last 7 Days",
		value: [addDays(new Date(), -7), new Date()],
		placement: "left",
	},
	{
		label: "Last 30 Days",
		value: [addDays(new Date(), -30), new Date()],
		placement: "left",
	},
];

const TableHead = ({ data, sendDataToParent, resetFilter, productData, customerTypeData, packageData }) => {
	// Define state to store input value
	const [inputValue, setInputValue] = useState({});
	const [inputTemp, setInputTemp] = useState({});
	const [filterColor, setFilterColor] = useState({});
	const [showFilter, setShowFilter] = useState({});
	const [short, setShort] = useState("created_date");
	const [order, setOrder] = useState("desc");
	const [showShortIcon, setShowShortIcon] = useState(true);
	const inputRef = useRef(null);

	/**
	 *	@param - Table Heading List
	 * Sorting the data array based on the sequence property and render table heading
	 */
	const sortedData = Array.isArray(data) ? data.sort((a, b) => parseInt(a.sequence) - parseInt(b.sequence)) : [];

	// Object to track sort state for each field Start
	// Dynamically initialize sortStates based on sortedData fields
	const initializeSortStates = () => {
		const initialState = {};
		sortedData.forEach((item) => {
			if (item.field == "created_date") {
				initialState[item.field] = "down";
			} else {
				initialState[item.field] = "default"; // Set default state for each field
			}
		});
		return initialState;
	};

	// Use useState to initialize sort states
	const [sortStates, setSortStates] = useState(() => initializeSortStates());
	// Ref to keep track of the previous sortedData
	const prevSortedDataRef = useRef();

	useEffect(() => {
		// Check if sortedData has actually changed
		if (JSON.stringify(prevSortedDataRef.current) !== JSON.stringify(sortedData)) {
			setSortStates(initializeSortStates());
			prevSortedDataRef.current = sortedData;
		}
	}, [sortedData]);

	// Object to track sort state for each field End

	/**
	 *	@param - Table Column Filter
	 * @param {*} component - addClumnFilter
	 * @param {*} component - Input field onchange
	 */
	// Define the debounce function

	const debounceOwn = (func, delay) => {
		let timer;
		return function (...args) {
			const context = this;
			if (timer) clearTimeout(timer);
			timer = setTimeout(() => {
				timer = null;
				func.apply(context, args);
			}, delay);
		};
	};
	// Define the search handler function
	const filterInputHandler = (name, value) => {
		//const { name, value } = target;
		if (name === "product") {
			setInputValue((prevInputValue) => ({
				...prevInputValue,
				[name]: [...inputValue.product, value], // Update inputValue with the selected values
			}));
		}
		if (name === "customer_type") {
			setInputValue((prevInputValue) => ({
				...prevInputValue,
				[name]: [...inputValue.customer_type, value.toLowerCase()], // Update inputValue with the selected values
			}));
		} else {
			// For other inputs, simply update the value
			setInputValue((prevInputValue) => ({
				...prevInputValue,
				[name]: value, // Update inputValue with the selected values
			}));
			if (value !== "" && value.length > 0) {
				setFilterColor((prev) => ({
					...prev,
					[name]: name,
				}));
			} else {
				// Copy the previous state
				const updatedFilterColor = { ...filterColor };
				// Remove the name key
				delete updatedFilterColor[name];
				// Update the state
				setFilterColor(updatedFilterColor);
			}
		}
	};

	// Memoize the debounced search handler using useCallback
	const debouncedFilterInputHandler = useCallback(debounceOwn(filterInputHandler, 500), []);
	// Handle input change
	const handleInputChange = (name) => (value) => {
		debouncedFilterInputHandler(name, value);
		setInputTemp((prevInputValue) => ({
			...prevInputValue,
			[name]: value, // Update inputValue with the selected values
		}));
	};

	/**
	 *	@param - Table Column Filter
	 * @param {*} component - addClumnFilter
	 * @param {*} component - Date range field
	 */

	const handleApplyTime = (name) => (value) => {
		const formattedDates = value.map((date) => moment(date).format("YYYY-MM-DD"));
		let finalStartDate = formattedDates[0];
		let finalEndDate = formattedDates[1];

		setInputValue((prevInputValue) => ({
			...prevInputValue,
			[name]: `${finalStartDate} ~ ${finalEndDate}`, // Update inputValue with the selected values
		}));
		setInputTemp((prevInputValue) => ({
			...prevInputValue,
			[name]: value, // Update inputValue with the selected values
		}));
		if (value !== "" && value.length > 0) {
			setFilterColor((prev) => ({
				...prev,
				[name]: name,
			}));
		} else {
			// Copy the previous state
			const updatedFilterColor = { ...filterColor };
			// Remove the name key
			delete updatedFilterColor[name];
			// Update the state
			setFilterColor(updatedFilterColor);
		}
	};
	const handleCleanDate = (name) => (value) => {
		setInputValue((prevInputValue) => ({
			...prevInputValue,
			[name]: "", // Update inputValue with the selected values
		}));
		setInputTemp((prevInputValue) => ({
			...prevInputValue,
			[name]: null, // Update inputValue with the selected values
		}));

		// Copy the previous state
		const updatedFilterColor = { ...filterColor };
		// Remove the name key
		delete updatedFilterColor[name];
		// Update the state
		setFilterColor(updatedFilterColor);
		// Perform any other actions when the date range is cleaned
	};
	const handleChangeDate = (name) => (value) => {
		// `value` will contain the new date range selection
		if (!value) {
			setInputValue((prevInputValue) => ({
				...prevInputValue,
				[name]: "", // Update inputValue with the selected values
			}));
			setInputTemp((prevInputValue) => ({
				...prevInputValue,
				[name]: null, // Update inputValue with the selected values
			}));

			// Copy the previous state
			const updatedFilterColor = { ...filterColor };
			// Remove the name key
			delete updatedFilterColor[name];
			// Update the state
			setFilterColor(updatedFilterColor);
		} else {
			// Perform any other actions based on the new date range value
			const formattedDates = value.map((date) => moment(date).format("YYYY-MM-DD"));
			let finalStartDate = formattedDates[0];
			let finalEndDate = formattedDates[1];

			setInputValue((prevInputValue) => ({
				...prevInputValue,
				[name]: `${finalStartDate} ~ ${finalEndDate}`, // Update inputValue with the selected values
			}));
			setInputTemp((prevInputValue) => ({
				...prevInputValue,
				[name]: value, // Update inputValue with the selected values
			}));
			setFilterColor((prev) => ({
				...prev,
				[name]: name,
			}));
		}
	};

	/**
	 *	@param - Table Column Filter
	 * @param {*} component - addClumnFilter
	 * @param {*} component -  react multiple select box
	 */

	const getReactSelectData = (formDataUpdated) => {
		setInputValue({ ...inputValue, ...formDataUpdated });
		setInputTemp({ ...inputTemp, ...formDataUpdated });
		//console.log(formDataUpdated, "get data");
		//sendDataToParent(inputValue);
	};
	const handleSelectChange = (name) => (value) => {
		setInputValue((prevInputValue) => ({
			...prevInputValue,
			[name]: value, // Update inputValue with the selected values
		}));
		setInputTemp((prevInputValue) => ({
			...prevInputValue,
			[name]: [...value], // Update inputValue with the selected values
		}));
		if (value.length > 0) {
			setFilterColor((prev) => ({
				...prev,
				[name]: name,
			}));
		} else {
			// Copy the previous state
			const updatedFilterColor = { ...filterColor };
			// Remove the name key
			delete updatedFilterColor[name];
			// Update the state
			setFilterColor(updatedFilterColor);
		}
	};

	useEffect(() => {
		sendDataToParent(inputValue, short, order);
	}, [inputValue, short, order]);
	/**
	 * Reset All Filter Code Start
	 *Reset All filter state in click on reset button on parent
	 */

	useEffect(() => {
		// Reset the state value in the child component
		setInputValue({});
		setInputTemp({});
		setFilterColor({});
		setShowFilter({});
	}, [resetFilter]); //inputValue

	/**
	 * Reset All Filter Code End
	 *
	 */
	/**
	 * Filter  Open & close Start
	 *
	 */
	useEffect(() => {
		function handleClickOutside(event) {
			if (inputRef.current && !inputRef.current.contains(event.target)) {
				setShowFilter({});
			}
		}

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [inputRef]);

	/**
	 *
	 * @param {*} field
	 * filter open and close on click
	 */
	function handleFilter(field) {
		const filterElement = document.querySelector(`.${field}`);
		if (filterElement) {
			if (filterElement.style.display === "none") {
				//filterElement.style.display = "block";
				setShowFilter((prev) => ({
					...prev,
					[field]: true,
				}));
			} else {
				//filterElement.style.display = "none";
				setShowFilter((prev) => ({
					...prev,
					[field]: false,
				}));
			}
		}
	}
	/**
	 * Filter  Open & close End
	 *
	 */

	/**
	 *
	 * @param {*} attribute_type
	 * @param {*} headerName
	 * @param {*} field
	 * @param {*} option
	 * @returns  Table Header Filter UI
	 */

	const addClumnFilter = (attribute_type, headerName, field, option = []) => {
		if (attribute_type == "text") {
			if (field == "package") {
				return (
					<div
						className={`row ${field}`}
						style={{
							display: showFilter[field] ? "block" : "none",
						}}
					>
						<div className="col-12 myDiv">
							<CheckPicker
								name={field}
								data={packageData.map((item) => {
									return {
										label: item.value,
										value: item.id,
									};
								})}
								value={inputTemp.hasOwnProperty(field) ? inputTemp[field] : []}
								onChange={handleSelectChange(field)}
								style={{ width: "100%" }}
								aria-haspopup="listbox"
								className={field}
								menuClassName={`${field}-rs-picker-popup`}
							/>
						</div>
					</div>
				);
			} else {
				return (
					<div
						className={`row ${field}`}
						style={{
							display: showFilter[field] ? "block" : "none",
						}}
					>
						<div className="col-12">
							<InputGroup inside>
								<Input
									placeholder={`Search ${headerName}`}
									onChange={handleInputChange(field)}
									value={inputTemp.hasOwnProperty(field) ? inputTemp[field] : ""}
								/>
								<InputGroup.Button>
									<SearchIcon />
								</InputGroup.Button>
							</InputGroup>
						</div>
					</div>
				);
			}
		} else if (attribute_type == "api" && field == "product_key") {
			return (
				<div className={`row ${field}`} style={{ display: showFilter[field] ? "block" : "none" }}>
					<div className="col-12">
						<CheckPicker
							name={field}
							data={productData.map((item) => {
								return {
									label: item.product_name,
									value: item.product_key,
								};
							})}
							value={inputTemp.hasOwnProperty(field) ? inputTemp[field] : []}
							onChange={handleSelectChange(field)}
							style={{ width: "100%" }}
							aria-haspopup="listbox"
							className={field}
							menuClassName={`${field}-rs-picker-popup`}
						/>
					</div>
				</div>
			);
		} else if (attribute_type == "customer_type") {
			// const optionsArray = Object.keys(option).map((key) => ({
			// 	id: key,
			// 	...option[key],
			// }));
			return (
				<div className={`row ${field}`} style={{ display: showFilter[field] ? "block" : "none" }}>
					<div className="col-12 myDiv">
						<CheckPicker
							name={field}
							data={customerTypeData.map((item) => {
								return {
									label: item.value,
									value: item.id,
								};
							})}
							value={inputTemp.hasOwnProperty(field) ? inputTemp[field] : []}
							onChange={handleSelectChange(field)}
							style={{ width: "100%" }}
							aria-haspopup="listbox"
							className={field}
							menuClassName={`${field}-rs-picker-popup`}
						/>
					</div>
				</div>
			);
		} else if (attribute_type == "date") {
			return (
				<div className={`row ${field}`} style={{ display: showFilter[field] ? "block" : "none" }}>
					<div className="col-12">
						<DateRangePicker
							showOneCalendar
							ranges={predefinedRanges}
							onOk={handleApplyTime(field)}
							onClean={handleCleanDate(field)}
							onChange={handleChangeDate(field)}
							value={inputTemp.hasOwnProperty(field) ? inputTemp[field] : null}
						/>
					</div>
				</div>
			);
		} else {
			return (
				<div className={`row ${field}`} style={{ display: showFilter[field] ? "block" : "none" }}>
					<div className="col-12">
						<InputGroup inside>
							<Input
								placeholder={`Search ${headerName}`}
								onChange={handleInputChange(field)}
								value={inputTemp.hasOwnProperty(field) ? inputTemp[field] : ""}
							/>
							<InputGroup.Button>
								<SearchIcon />
							</InputGroup.Button>
						</InputGroup>
					</div>
				</div>
			);
		}
	};
	const handleDescShort = (field) => {
		setShowShortIcon(false);
		setShort(field);
		setOrder("desc");
	};
	const handleAscShort = (field) => {
		setShowShortIcon(true);
		setShort(field);
		setOrder("asc");
	};

	const addShortFilter = (field) => {
		// return short == field && showShortIcon ? (
		// 	<BiSortAZ style={{ fontSize: "18px", color: short === field ? "#52b7ed" : "" }} onClick={() => handleDescShort(field)} />
		// ) : (
		// 	<BiSortZA style={{ fontSize: "18px", color: short === field ? "#52b7ed" : "" }} onClick={() => handleAscShort(field)} />
		// );

		const sortClass = sortStates[field] ? sortTypes[sortStates[field]].class : sortTypes["default"].class;
		return (
			<i className={`fas fa-${sortClass}`} onClick={() => onSortChange(field)} style={getIconStyle(field)}></i>
		);
	};

	// Function to handle sorting change
	const onSortChange = (field) => {
		let nextSort;
		if (sortStates[field] === "down") {
			nextSort = "up";
			handleAscShort(field); // Ascending sorting
		} else if (sortStates[field] === "up") {
			nextSort = "down";
			handleDescShort(field); // Descending sorting
		} else if (sortStates[field] === "default") {
			nextSort = "down";
			handleAscShort(field); // Ascending sorting
		}

		// Update the sort state only for the clicked field, reset others to "default"
		setSortStates((prevState) => ({
			...Object.keys(prevState).reduce((acc, key) => {
				acc[key] = key === field ? nextSort : "down";
				return acc;
			}, {}),
		}));
	};

	const sortTypes = {
		default: { class: "sort", fn: (a, b) => a },
		up: { class: "sort-up", fn: (a, b) => a - b },
		down: { class: "sort-down", fn: (a, b) => b - a },
	};

	// Determine icon color based on whether it's active
	const getIconStyle = (field) => {
		return sortStates[field] !== "default"
			? { color: "#52b7ed" } // Active icon color
			: { color: "black" }; // Default icon color
	};

	return (
		<thead>
			<tr>
				{sortedData.map((item) => (
					<th width={item.width} key={item._id}>
						<div className="d-flex align-items-center justify-content-between parentDiv">
							{item.headerName}

							{item.is_filter ? (
								<div className={` openDropdown`}>
									{/*addShortFilter(item.field)*/}
									<BsFilter
										style={{
											fontSize: "22px",
										}}
										data-toggle="dropdown"
										aria-haspopup="true"
										aria-expanded="false"
										onClick={() => handleFilter(item.field)}
										className={filterColor[item.field] === item.field ? "selected-filter" : ""}
									/>

									<div className="filter-parent">
										{addClumnFilter(item.attribute_type, item.headerName, item.field, item.options)}
									</div>
								</div>
							) : (
								""
							)}

							{item.sortable ? addShortFilter(item.field) : ""}
						</div>
					</th>
				))}
				{sortedData.length > 0 && (
					<th width="30" className="fixed-col">
						<div className="d-flex align-items-center justify-content-between"></div>
					</th>
				)}
			</tr>
		</thead>
	);
};

export default TableHead;



// WEBPACK FOOTER //
// ./src/components/crm/table/TableHead.jsx